import { Component, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronRight } from '@ng-icons/lucide';
import { V2_ICONS } from '@v2/v2.icons';
import { MenuItem } from '../menu.model';

/**
 * One row of the rail. Ported from evo-accounts
 * `layout/sidebar/sidebar-menu-item.component.ts`.
 *
 * Three renderings of the same row, chosen by what the item is: an outbound
 * `href`, an in-app `route`, or a plain button that folds children open. The
 * heading form is a group label inside a section, and the `desk` form is a
 * labelled block of contact lines.
 *
 * Adaptations, and only these:
 *
 *  - icon names are the `V2_ICONS` keys (`lucideChevronDown`) rather than
 *    evo-accounts' aliases (`chevron-down`), because this app's icon registry
 *    is keyed that way and the nav data already speaks it;
 *  - `routerLinkActiveOptions` is exact for `/v2` rather than for `/`, because
 *    `/v2` is this app's root route. Without it the estimator's own entry stays
 *    lit on every child route, since routerLinkActive matches on prefix;
 *  - `CommonModule` is not imported. The original brought it in for `[class]`
 *    and `[ngClass]`; neither is a CommonModule directive, and everything else
 *    in the template is built-in control flow.
 */
@Component({
  selector: 'app-v2-rail-menu-item',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, NgIcon],
  providers: [provideIcons({ ...V2_ICONS, lucideChevronRight })],
  template: `
    @if (item().type === 'heading') {
      @if (item().children && item().children!.length > 0) {
        <div class="desk">
          <p class="desk-name">{{ item().label }}</p>
          <div class="desk-lines">
            @for (child of item().children; track child.id) {
              <app-v2-rail-menu-item [item]="child" (itemClick)="itemClick.emit($event)" />
            }
          </div>
        </div>
      } @else {
        <div class="menu-heading">
          {{ item().label }}
        </div>
      }
    } @else if (item().href) {
      <a
        [href]="item().href"
        [attr.target]="item().newTab ? '_blank' : null"
        [attr.rel]="item().newTab ? 'noopener noreferrer' : null"
        class="menu-link"
        [class.disabled]="item().disabled"
        [attr.aria-label]="item().label"
        [attr.title]="item().tooltip || item().label"
        [attr.tabindex]="item().disabled ? -1 : 0"
        (click)="handleClick($event)">
        <ng-icon [name]="item().icon" class="icon" />
        <span class="label" [class.label--data]="item().mono">{{ item().label }}</span>
        @if (item().isExternal) {
          <ng-icon name="lucideExternalLink" class="external-icon" aria-hidden="true" />
        }
        @if (item().badge) {
          <span [class]="'menu-badge ' + item().badge!.class">
            {{ item().badge!.text }}
          </span>
        }
      </a>
    } @else if (item().route) {
      <a
        [routerLink]="[item().route]"
        routerLinkActive="active"
        [routerLinkActiveOptions]="{ exact: item().route === '/' }"
        class="menu-link"
        [class.disabled]="item().disabled"
        [attr.aria-label]="item().label"
        [attr.title]="item().tooltip || item().label"
        [attr.tabindex]="item().disabled ? -1 : 0"
        (click)="handleClick($event)">
        <ng-icon [name]="item().icon" class="icon" />
        <span class="label">{{ item().label }}</span>
        @if (item().badge) {
          <span [class]="'menu-badge ' + item().badge!.class">
            {{ item().badge!.text }}
          </span>
        }
      </a>
    } @else {
      <button
        type="button"
        class="menu-link"
        [class.disabled]="item().disabled"
        [attr.aria-label]="item().label"
        [attr.title]="item().tooltip || item().label"
        [attr.aria-expanded]="isExpanded()"
        [attr.tabindex]="item().disabled ? -1 : 0"
        (click)="handleClick($event)">
        <ng-icon [name]="item().icon" class="icon" />
        <span class="label">{{ item().label }}</span>
        @if (item().children && item().children!.length > 0) {
          <ng-icon
            [name]="isExpanded() ? 'lucideChevronDown' : 'lucideChevronRight'"
            class="chevron-icon"
            aria-hidden="true" />
        }
        @if (item().badge) {
          <span [class]="'menu-badge ' + item().badge!.class">
            {{ item().badge!.text }}
          </span>
        }
      </button>
    }

    @if (item().children && item().children!.length > 0 && isExpanded()) {
      <ul class="submenu" role="menu">
        @for (child of item().children; track child.id) {
          <li class="submenu-item" role="none">
            <app-v2-rail-menu-item
              [item]="child"
              [isExpanded]="false"
              (itemClick)="itemClick.emit($event)" />
          </li>
        }
      </ul>
    }
  `,
  styles: [
    `
      /* One row of the rail. Every colour comes from the --sidebar-*
         properties the parent sets from tokens, so this file holds shape only.

         The class names here are load-bearing beyond CSS: .menu-link, .submenu
         and .disabled are what MenuKeyboardNavigationDirective queries to walk
         the menu with the arrow keys. */
      :host {
        display: block;
      }

      /* Metrics from evo-tss's .rail a: 9px/10px padding, an 11px gap, and a
         rounded corner, so each row is a pill inside the rail's gutter rather
         than a full-bleed band. The row comes out at 38px, and no floor is
         stated here — see the coarse-pointer block at the foot of this file. */
      .menu-link {
        display: flex;
        align-items: center;
        gap: 11px;
        padding: 9px 10px;
        border-radius: var(--r-sm);
        font-size: 0.84375rem; /* evo-tss's 13.5px */
        font-weight: 500;
        color: var(--sidebar-text-color);
        text-decoration: none;
        transition:
          background-color 0.15s ease,
          color 0.15s ease;
        border: none;
        background: transparent;
        width: 100%;
        text-align: left;
        position: relative;
        cursor: pointer;

        &:hover:not(.disabled) {
          background-color: var(--sidebar-item-hover-bg);
          color: var(--sidebar-text-hover);
        }

        /* Where you are: a tint of the brand, with the label going navy and
           bolder inside it. On a dark rail this needed a 2px edge as well,
           because a wash on navy was too close to hover to tell apart; against
           the light surface the hue change carries it on its own. */
        &.active {
          background-color: var(--sidebar-item-active-bg);
          color: var(--sidebar-item-active-ink);
          font-weight: 600;
        }

        &.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .icon {
          --ng-icon__size: 20px;
          width: 20px;
          height: 20px;
          font-size: 20px;
          min-width: 20px;
          flex-shrink: 0;
        }

        .label {
          flex: 1;
          min-width: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          opacity: 1;
          transition:
            opacity 0.3s ease,
            transform 0.3s ease;
        }

        /* A number that gets read digit by digit, not a word: the data face,
           tabular figures and a little tracking to keep the groups apart. */
        .label--data {
          font-family: var(--f-data);
          font-variant-numeric: tabular-nums;
          font-size: 0.8125rem;
          letter-spacing: 0.04em;
        }

        /* Pushed to the trailing edge, as evo-tss does with its .out arrow. */
        .external-icon,
        .chevron-icon {
          margin-left: auto;
          --ng-icon__size: 14px;
          width: 14px;
          height: 14px;
          font-size: 14px;
          flex-shrink: 0;
          color: var(--sidebar-text-muted);
        }

        /* A count, not a state: solid brand fill so it holds its own against
           the row's own active wash, which it would otherwise share a colour
           with. */
        .menu-badge {
          margin-left: 8px;
          padding: 2px 7px;
          border-radius: var(--r-pill);
          background: var(--c-navy-fill);
          color: var(--c-on-navy-fill);
          font-family: var(--f-display);
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
      }

      :host .menu-link:focus-visible {
        outline: 2px solid var(--sidebar-focus-ring);
        outline-offset: -2px;
      }

      .submenu {
        list-style: none;
        padding: 0;
        margin: 0 0 0 20px;

        .submenu-item {
          margin-bottom: 2px;
        }
      }

      .menu-heading {
        padding: 8px 10px 2px;
        margin-top: 14px;
        font-family: var(--f-display);
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--sidebar-text-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* First heading inside a menu list shouldn't push away from the section
         header. */
      :host-context(.menu-item:first-child) .menu-heading {
        margin-top: 0;
      }

      /* A 24/7 desk, laid out as evo-tss's support desks lay it out in the rail
         (its "plain" variant): the desk name, then one labelled row per channel
         — the number and the address ARE the link text.

         No card and no green dot. The rail is already a surface, so a second
         bordered box inside it was chrome around chrome; and the dot was
         claiming "staffed" a second time, on top of a section header that
         already says 24/7. */
      .desk {
        margin: 0 0 18px;
        padding: 0 10px;
      }

      :host-context(.menu-item:last-child) .desk {
        margin-bottom: 0;
      }

      .desk-name {
        margin: 0 0 4px;
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--sidebar-text-hover);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* Compact variant — applied when the parent section has compact: true. */
      :host-context(.menu-section--compact) {
        .menu-link {
          padding: 5px 10px;
          gap: 8px;
          font-size: 0.8125rem;

          .icon {
            --ng-icon__size: 16px;
            width: 16px;
            height: 16px;
            font-size: 16px;
            min-width: 16px;
          }
        }

        .menu-heading {
          padding: 6px 10px 2px;
        }
      }

      /* A desk's channel rows. Same component, so :host-context is what reaches
         them — they are still .menu-link and still walk with the arrow keys,
         they just drop the pill: a desk row is not a place you navigate to, and
         a hover wash on three stacked rows made the block look like a fourth
         menu. Declared after the compact block so it wins over it. */
      :host-context(.desk-lines) .menu-link {
        gap: 11px;
        padding: 7px 0;
        border-radius: 0;
        color: var(--sidebar-text-muted);
        font-size: 0.84375rem;

        .icon {
          --ng-icon__size: 15px;
          width: 15px;
          height: 15px;
          font-size: 15px;
          min-width: 15px;
        }

        /* An address outruns the rail before the number does — break its tail
           rather than widen the rail. */
        .label {
          white-space: normal;
          overflow-wrap: anywhere;
        }
      }

      :host-context(.desk-lines) .menu-link:hover:not(.disabled) {
        background-color: transparent;
        color: var(--sidebar-item-active-ink);
      }

      /* Rows grow to a 44px minimum for a thumb, and only for a thumb.
         evo-accounts raises them below 767.98px, because that is where its rail
         becomes a phone drawer; this rail is never a drawer — the shell drops it
         at 880px and the tab bar takes over — so the width tells us nothing
         about what is pointing at it. A tablet in landscape is 1024px wide and
         still a thumb. On a fine pointer the row keeps its natural 38px, which
         is what evo-accounts shows on a desktop. */
      @media (pointer: coarse) {
        .menu-link {
          min-height: 44px;
        }

        :host-context(.desk-lines) .menu-link {
          min-height: 40px;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .menu-link,
        .menu-link * {
          transition: none !important;
        }
      }
    `,
  ],
})
export class V2RailMenuItemComponent {
  readonly item = input.required<MenuItem>();
  readonly isExpanded = input<boolean>(false);
  readonly itemClick = output<MenuItem>();

  handleClick(event: Event) {
    if (this.item().disabled) {
      event.preventDefault();
      return;
    }

    this.itemClick.emit(this.item());
  }
}
